import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
// Typechecks once @tranquility-ai/theme-components 3.9.0 is pinned.
import { Checkbox } from '@tranquility-ai/theme-components';
import { Popover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import { FeaturesContext, useReactiveFeatureFlags, useReactiveLdFlags, useReactiveServiceFlags } from '@/app/contexts';
import type { FeatureFlagTypes } from '@/shared/types';
import {
  MEDIA_PLAYER_TRANSITION_FLAG_CAMEL,
  MEDIA_PLAYER_TRANSITION_FLAG_KEY,
  MEDIA_PLAYER_TRANSITION_LABELS,
  MEDIA_PLAYER_TRANSITION_VIEWERS,
  readMediaPlayerTransitionFlag,
  type MediaPlayerTransitionViewer,
} from '@/shared/media-player-transition';
import { ArrowCounterClockwiseIcon, CheckIcon, CloudIcon, EqualsIcon, NotEqualsIcon, ProhibitIcon, ToolboxIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Modal } from '@tranquility-ai/theme-components/modal';
import '@tranquility-ai/theme-components/modal.css';
import { JsonViewer } from '@tranquility-ai/theme-components/json-viewer';
import '@tranquility-ai/theme-components/json-viewer.css';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import {
  isSimonSaysEnabled,
  useTaiFeatureFlagsFetchFailure,
  useTaiFeatureFlagsRequestBody,
} from '@/lib/tai-feature-flags';
import {
  alignedFlagKeys,
  flagEditorKind,
  flagHasStoredOverride,
  flagIsPresent,
  nonBooleanOverrideDiffers,
  flagValueDraft,
  flagValuesEqual,
  isMediaPlayerTransitionFlagKey,
  mediaTransitionEditorValue,
  mediaTransitionModelsEqual,
  mediaTransitionOrigin,
  nextMediaTransitionOverride,
  parseFlagOverrideDraft,
  sessionMediaTransitionValue,
  type FlagEditorKind,
} from './internal-tools-flags';
import { useSyncStatus, SyncStatusIndicator } from '@/components/ui/sync-status';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { PROTOTYPE_FLAGS } from './prototypes/prototype-flags';
import {
  statusBarIconButtonClassName,
  statusBarIconButtonProps,
} from './status-bar-icon-button';

export type { SyncStatus } from '@/components/ui/sync-status';

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

/** Text for the value popup. Objects, arrays, and JSON strings are pretty-printed. */
function flagValueContent(value: FeatureFlagTypes): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        if (parsed !== null && typeof parsed === 'object') {
          return JSON.stringify(parsed, null, 2);
        }
      } catch {
        // Not JSON. Show the string as stored.
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

/** LaunchDarkly active, Simon Says visible: source, override, compare, Simon Says. */
const flagGridLdActiveClassName = 'grid grid-cols-[minmax(0,1fr)_1px_168px_1px_28px_1px_minmax(0,1fr)] items-stretch gap-x-2';
/** Simon Says active: LaunchDarkly, compare, Simon Says, session override. */
const flagGridSimonSaysActiveClassName = 'grid grid-cols-[minmax(0,1fr)_1px_28px_1px_minmax(0,1fr)_1px_168px] items-stretch gap-x-2';
const flagGridLdOnlyClassName = 'grid grid-cols-[minmax(0,1fr)_1px_168px] items-stretch gap-x-2';

function FlagColumnDivider() {
  return <div className="w-px self-stretch bg-white/50" aria-hidden />;
}

function ResetOverrideButton({ flagKey, onReset }: { flagKey: string; onReset: () => void }) {
  const t = useTimePilotT();
  return (
    <button
      type="button"
      className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-0.5 text-inherit"
      aria-label={t('internalTools.resetFlagOverrideFor', { key: flagKey })}
      onClick={onReset}
    >
      <ArrowCounterClockwiseIcon size={16} weight="bold" aria-hidden />
    </button>
  );
}

function OverrideMark() {
  const t = useTimePilotT();
  return (
    <span
      className="text-base font-bold leading-none text-green-500"
      role="img"
      aria-label={t('internalTools.overridingFlagValue')}
    >
      !
    </span>
  );
}

function FlagCompareMark({ equal }: { equal: boolean }) {
  const Icon = equal ? EqualsIcon : NotEqualsIcon;
  return (
    <Icon
      size={16}
      weight="bold"
      className={equal ? 'text-green-500' : 'text-amber-400'}
      aria-hidden
    />
  );
}

function MissingFlagMark({ flagKey }: { flagKey: string }) {
  const t = useTimePilotT();
  return (
    <ProhibitIcon
      size={16}
      weight="bold"
      className="text-red-500"
      aria-label={t('internalTools.notSet', { key: flagKey })}
    />
  );
}

function ServiceFlagTooltip({
  requestBody,
  children,
}: {
  requestBody: string | null;
  children: ReactNode;
}) {
  const t = useTimePilotT();
  let posted: unknown = requestBody;
  if (requestBody) {
    try {
      posted = JSON.parse(requestBody);
    } catch {
      posted = requestBody;
    }
  }
  return (
    <TpTooltip
      variant="inverted"
      placement="left"
      delayDuration={200}
      content={
        <div className="max-w-[420px] text-left">
          <div className="mb-1 text-xs font-semibold">{t('internalTools.postBody')}</div>
          <pre className="m-0 max-h-[220px] overflow-auto whitespace-pre-wrap break-all text-xs">
            {requestBody ? prettyJson(posted) : t('internalTools.noPostYet')}
          </pre>
        </div>
      }
    >
      {children}
    </TpTooltip>
  );
}

function ServiceFlagValue({
  flagKey,
  value,
  onView,
}: {
  flagKey: string;
  value: FeatureFlagTypes;
  onView: (key: string, value: FeatureFlagTypes) => void;
}) {
  const t = useTimePilotT();
  if (typeof value === 'boolean') {
    return value ? (
      <CheckIcon size={16} className="text-green-500 -mb-1" />
    ) : (
      <XIcon size={16} className="text-red-500 -mb-1" />
    );
  }
  return (
    <Button
      type="button"
      size="sm"
      style="solid"
      color="secondary"
      aria-label={t('internalTools.viewFlagValueFor', { key: flagKey })}
      onClick={() => onView(flagKey, value)}
    >
      {t('internalTools.viewFlagValue')}
    </Button>
  );
}

function FlagSide({
  flagKey,
  value,
  present,
  onView,
}: {
  flagKey: string;
  value: FeatureFlagTypes | undefined;
  present: boolean;
  onView: (key: string, value: FeatureFlagTypes) => void;
}) {
  const on = value === true;
  return (
    <div className="flex h-full min-w-0 flex-row items-center justify-between gap-2 py-1">
      <div className={`min-w-0 break-all ${present && typeof value === 'boolean' ? (on ? 'text-green-500' : 'text-red-500') : ''}`}>
        {flagKey}
      </div>
      <div className="flex shrink-0 flex-row items-center justify-end">
        {present && value !== undefined ? (
          <ServiceFlagValue flagKey={flagKey} value={value} onView={onView} />
        ) : (
          <MissingFlagMark flagKey={flagKey} />
        )}
      </div>
    </div>
  );
}

function ComparePane({
  label,
  flagKey,
  value,
  present,
}: {
  label: string;
  flagKey: string;
  value: FeatureFlagTypes | undefined;
  present: boolean;
}) {
  const t = useTimePilotT();
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="text-sm font-semibold">{label}</div>
      {present && value !== undefined ? (
        <pre className="m-0 max-h-[min(50vh,420px)] overflow-auto whitespace-pre-wrap break-all rounded-md bg-slate-900 p-3 text-xs text-white">
          {flagValueContent(value)}
        </pre>
      ) : (
        <div className="flex items-center gap-2 text-sm text-red-500">
          <MissingFlagMark flagKey={flagKey} />
          <span>{t('internalTools.notReturned')}</span>
        </div>
      )}
    </div>
  );
}

type FlagCompare = {
  key: string;
  ld: FeatureFlagTypes | undefined;
  service: FeatureFlagTypes | undefined;
  ldPresent: boolean;
  servicePresent: boolean;
};

export function InternalTools() {
  const t = useTimePilotT();
  const [isOpen, setIsOpen] = useState(false);
  const [viewingFlag, setViewingFlag] = useState<{ key: string; value: FeatureFlagTypes } | null>(null);
  const [comparingFlag, setComparingFlag] = useState<FlagCompare | null>(null);
  const [editingFlag, setEditingFlag] = useState<{ key: string; kind: FlagEditorKind } | null>(null);
  const [overrideDraft, setOverrideDraft] = useState('');
  const [overrideDraftError, setOverrideDraftError] = useState(false);
  // Set in the click handler, before the modal takes focus. The tools popover
  // closes when focus leaves it; ignore that close while a value modal is open.
  const viewingFlagRef = useRef(viewingFlag);
  const comparingFlagRef = useRef(comparingFlag);
  const editingFlagRef = useRef(editingFlag);
  const featuresContext = useContext(FeaturesContext);
  const setFlagOverrides = featuresContext?.setFlagOverrides;
  // The context object is stable, so reading overrides during render goes
  // stale until something else re-renders this panel. Keep a snapshot that
  // updates on the same notification as the flag maps.
  const [flagOverrides, setFlagOverridesSnapshot] = useState<{ [key: string]: FeatureFlagTypes }>(
    () => featuresContext?.getFlagOverrides() ?? {},
  );
  useEffect(() => {
    if (!featuresContext?.subscribe || !featuresContext.getFlagOverrides) return;
    const pull = () => setFlagOverridesSnapshot(featuresContext.getFlagOverrides());
    pull();
    return featuresContext.subscribe(pull);
  }, [featuresContext]);
  // Checkbox edits and JSON saves share this object. It updates in this
  // component, so the flag row and the bottom rows stay together even when
  // the merged flag map has not been replaced yet.
  const [mediaEdit, setMediaEdit] = useState<FeatureFlagTypes | null>(null);
  const mediaEditRef = useRef<FeatureFlagTypes | null>(null);
  // Subscribe so checkbox state tracks overrides immediately even though
  // FeaturesContext identity is stable. The LaunchDarkly column has its own
  // subscription: while Simon Says is the live source, a LaunchDarkly update
  // does not change the merged map that `useReactiveFeatureFlags` reads.
  const featureFlags = useReactiveFeatureFlags();
  const flagsFromLD = useReactiveLdFlags();
  const flagsFromService = useReactiveServiceFlags();
  // LaunchDarkly simonSays gates the extra columns. False, missing, or any
  // non-boolean value keeps the list to LaunchDarkly and the current checkbox.
  const showSimonSaysColumns = isSimonSaysEnabled(flagsFromLD);
  const simonSaysActive = showSimonSaysColumns && isSimonSaysEnabled(flagsFromService);
  const simonSaysFetchFailure = useTaiFeatureFlagsFetchFailure();
  const simonSaysFailureReason = simonSaysFetchFailure?.kind === 'status'
    ? String(simonSaysFetchFailure.status)
    : simonSaysFetchFailure?.kind === 'unreachable'
      ? t('internalTools.unreachable')
      : null;
  const serviceRequestBody = useTaiFeatureFlagsRequestBody();
  const flagRows = alignedFlagKeys(
    flagsFromLD,
    showSimonSaysColumns ? flagsFromService : null,
  );
  const flagGridClassName = !showSimonSaysColumns
    ? flagGridLdOnlyClassName
    : simonSaysActive
      ? flagGridSimonSaysActiveClassName
      : flagGridLdActiveClassName;
  const { syncStatus, refetch } = useSyncStatus(!!featureFlags?.syncStatus);

  const openFlagValue = (key: string, value: FeatureFlagTypes) => {
    const next = { key, value };
    viewingFlagRef.current = next;
    setViewingFlag(next);
  };

  const closeFlagValue = () => {
    viewingFlagRef.current = null;
    setViewingFlag(null);
  };

  const openFlagCompare = (key: string) => {
    const next: FlagCompare = {
      key,
      ld: flagsFromLD?.[key],
      service: flagsFromService?.[key],
      ldPresent: flagIsPresent(flagsFromLD, key),
      servicePresent: flagIsPresent(flagsFromService, key),
    };
    comparingFlagRef.current = next;
    setComparingFlag(next);
  };

  const closeFlagCompare = () => {
    comparingFlagRef.current = null;
    setComparingFlag(null);
  };

  useEffect(() => {
    if (showSimonSaysColumns || !comparingFlagRef.current) return;
    comparingFlagRef.current = null;
    setComparingFlag(null);
  }, [showSimonSaysColumns]);

  const onFlagChange = (key: string, value: boolean) => {
    if (setFlagOverrides) {
      // Functional update so rapid toggles don't drop prior overrides when
      // reading a stale render snapshot of `flagOverrides`.
      setFlagOverrides((prev) => ({ ...prev, [key]: value }));
    }
  }

  const clearFlagOverride = (key: string) => {
    const keys = isMediaPlayerTransitionFlagKey(key)
      ? [MEDIA_PLAYER_TRANSITION_FLAG_CAMEL, MEDIA_PLAYER_TRANSITION_FLAG_KEY]
      : [key];
    if (isMediaPlayerTransitionFlagKey(key)) {
      mediaEditRef.current = null;
      setMediaEdit(null);
    }
    if (
      editingFlagRef.current
      && (editingFlagRef.current.key === key
        || (isMediaPlayerTransitionFlagKey(key) && isMediaPlayerTransitionFlagKey(editingFlagRef.current.key)))
    ) {
      closeFlagEditor();
    }
    const drop = (prev: { [key: string]: FeatureFlagTypes }) => {
      const next = { ...prev };
      let changed = false;
      for (const overrideKey of keys) {
        if (Object.prototype.hasOwnProperty.call(next, overrideKey)) {
          delete next[overrideKey];
          changed = true;
        }
      }
      return changed ? next : prev;
    };
    setFlagOverridesSnapshot(drop);
    setFlagOverrides?.(drop);
  }

  const externalMedia = sessionMediaTransitionValue(flagOverrides, featureFlags);
  // Local edit wins until the snapshot catches up, and after that they match.
  const shownMedia = mediaEdit ?? externalMedia;
  // Inherited `enabled` is written onto each viewer, so a checked box is the
  // same boolean the JSON editor shows for that field.
  const mediaSession = mediaTransitionEditorValue(shownMedia);
  const activeMedia = mediaTransitionEditorValue(
    simonSaysActive
      ? readMediaPlayerTransitionFlag(flagsFromService)
      : readMediaPlayerTransitionFlag(flagsFromLD),
  );
  const mediaOrigin = mediaEdit != null
    ? 'override'
    : mediaTransitionOrigin(flagOverrides, simonSaysActive);
  const mediaOriginLabel = mediaOrigin === 'override'
    ? t('internalTools.override')
    : mediaOrigin === 'simonSays'
      ? t('internalTools.simonSays')
      : t('internalTools.launchDarkly');
  const mediaValueText = shownMedia === undefined
    ? ''
    : flagValueDraft(shownMedia);

  const writeMediaTransitionOverride = (
    prev: { [key: string]: FeatureFlagTypes },
    next: FeatureFlagTypes,
  ) => ({
    ...prev,
    [MEDIA_PLAYER_TRANSITION_FLAG_CAMEL]: next,
    [MEDIA_PLAYER_TRANSITION_FLAG_KEY]: next,
  });

  const rememberMediaTransition = (nextValue: FeatureFlagTypes) => {
    mediaEditRef.current = nextValue;
    setMediaEdit(nextValue);
    if (editingFlagRef.current && isMediaPlayerTransitionFlagKey(editingFlagRef.current.key)) {
      const kind = flagEditorKind(nextValue) ?? 'json';
      setOverrideDraft(flagValueDraft(nextValue, kind === 'json'));
      setOverrideDraftError(false);
    }
  };

  const patchMediaTransition = (patch: Record<string, FeatureFlagTypes>) => {
    if (!setFlagOverrides) return;
    let nextValue: FeatureFlagTypes = patch;
    setFlagOverrides((prev) => {
      const seeded = mediaEditRef.current == null
        ? prev
        : writeMediaTransitionOverride(prev, mediaEditRef.current);
      const live = readMediaPlayerTransitionFlag(featuresContext?.getFeatureFlags?.() ?? featureFlags);
      nextValue = nextMediaTransitionOverride(seeded, live, patch);
      return writeMediaTransitionOverride(prev, nextValue);
    });
    rememberMediaTransition(nextValue);
  };

  const onMediaPlayerTransitionChange = (viewer: MediaPlayerTransitionViewer, value: boolean) => {
    patchMediaTransition({ [viewer]: value });
  };

  const mediaValueForEditor = () => (
    mediaEditRef.current
    ?? sessionMediaTransitionValue(
      featuresContext?.getFlagOverrides() ?? flagOverrides,
      featuresContext?.getFeatureFlags() ?? featureFlags,
    )
  );

  const openFlagEditor = (key: string, value: FeatureFlagTypes) => {
    const resolved = isMediaPlayerTransitionFlagKey(key)
      ? (mediaValueForEditor() ?? value)
      : value;
    const kind = flagEditorKind(resolved);
    if (!kind) return;
    const next = { key, kind };
    editingFlagRef.current = next;
    setEditingFlag(next);
    setOverrideDraft(flagValueDraft(resolved, kind === 'json'));
    setOverrideDraftError(false);
  };

  const closeFlagEditor = () => {
    editingFlagRef.current = null;
    setEditingFlag(null);
    setOverrideDraftError(false);
  };

  const saveFlagOverride = () => {
    if (!editingFlag || !setFlagOverrides) return;
    const parsed = parseFlagOverrideDraft(editingFlag.kind, overrideDraft);
    if (!parsed.ok) {
      setOverrideDraftError(true);
      return;
    }
    const { key } = editingFlag;
    const { value } = parsed;
    setFlagOverrides((prev) => (
      isMediaPlayerTransitionFlagKey(key)
        ? writeMediaTransitionOverride(prev, value)
        : { ...prev, [key]: value }
    ));
    if (isMediaPlayerTransitionFlagKey(key)) rememberMediaTransition(value);
    closeFlagEditor();
  };

  return (
    <>
    <Popover
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && (viewingFlagRef.current || comparingFlagRef.current || editingFlagRef.current)) return;
        setIsOpen(open);
      }}
      placement={featureFlags?.enableMediaAndSourceShare ? "top-start" : "bottom-end"}
      maxWidth={showSimonSaysColumns
        ? 'min(780px, calc(100vw - 20px))'
        : 'min(440px, calc(100vw - 20px))'}
      contentClassName="flex max-h-[calc(100vh-150px)] flex-col overflow-hidden [&_.tp-popover-body]:flex [&_.tp-popover-body]:min-h-0 [&_.tp-popover-body]:flex-1 [&_.tp-popover-body]:flex-col [&_.tp-popover-body]:overflow-hidden"
      content={
        <div className={`flex min-h-0 max-w-[calc(100vw-36px)] flex-1 flex-col gap-1 overflow-hidden pb-3 ${showSimonSaysColumns ? 'w-[760px]' : 'w-[420px]'}`}>
          <div className="flex flex-row items-center justify-between gap-2">
            <h2 className="my-[5px] text-2xl font-bold text-[var(--tp-popover-fg)]">{t('internalTools.title')}</h2>
            <div className="flex flex-row items-center gap-2">
              {featureFlags?.syncStatus ? (
                <div
                  onMouseEnter={refetch}
                  className="cursor-pointer box-border h-[30px] w-[30px] rounded-full border-[1px] border-solid border-gray-400 flex items-center justify-center"
                >
                  <SyncStatusIndicator
                    syncStatus={syncStatus}
                    iconSize={20}
                    fallback={<CloudIcon size={20} className="text-gray-500" aria-hidden />}
                  />
                </div>
              ) : null}
              <button
                type="button"
                aria-label={t('internalTools.close')}
                className="box-border flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full border border-solid border-gray-400 bg-transparent p-0 text-[var(--tp-popover-fg)]"
                onClick={() => setIsOpen(false)}
              >
                <XIcon size={16} weight="bold" />
              </button>
            </div>
          </div>

          {/* Feature Flags Section */}
          <h3 className="my-[5px] text-lg font-semibold text-[var(--tp-popover-fg)]">{t('internalTools.featureFlags')}</h3>
          <div className="flex w-full min-w-0 shrink-0 flex-col overflow-hidden rounded-md bg-slate-900 px-2 text-white">
            <div className={`${flagGridClassName} -mx-2 bg-slate-800 px-2`}>
              <div className="flex items-center py-3 font-bold">
                {t('internalTools.fromLd')}
                {simonSaysActive ? '' : t('internalTools.active')}
              </div>
              {simonSaysActive ? null : (
                <>
                  <FlagColumnDivider />
                  <div className="flex items-center justify-end py-3 pr-1 font-bold">{t('internalTools.current')}</div>
                </>
              )}
              {showSimonSaysColumns ? (
                <>
                  <FlagColumnDivider />
                  <div />
                  <FlagColumnDivider />
                  <div className="flex items-center py-3 font-bold">
                    {t('internalTools.simonSays')}
                    {simonSaysFailureReason
                      ? t('internalTools.simonSaysFailure', { reason: simonSaysFailureReason })
                      : ''}
                    {simonSaysActive ? t('internalTools.active') : ''}
                  </div>
                </>
              ) : null}
              {simonSaysActive ? (
                <>
                  <FlagColumnDivider />
                  <div className="flex items-center justify-end py-3 pr-1 font-bold">{t('internalTools.current')}</div>
                </>
              ) : null}
            </div>
            <div className="flex max-h-[400px] flex-col gap-2 overflow-y-scroll py-1 custom-scrollbar2">
              {flagRows.length === 0 ? (
                <div className="text-sm text-white/70">{t('internalTools.noFlags')}</div>
              ) : flagRows.map((key) => {
                const ldPresent = flagIsPresent(flagsFromLD, key);
                const servicePresent = flagIsPresent(flagsFromService, key);
                const ldValue = ldPresent ? flagsFromLD?.[key] : undefined;
                const serviceValue = servicePresent ? flagsFromService?.[key] : undefined;
                const equal = ldPresent && servicePresent && flagValuesEqual(ldValue, serviceValue);
                const activeSourceValue = simonSaysActive ? serviceValue : ldValue;
                const hasSessionValue = !!featureFlags && Object.prototype.hasOwnProperty.call(featureFlags, key);
                const rawSessionValue = hasSessionValue ? featureFlags?.[key] : activeSourceValue;
                const mediaRow = isMediaPlayerTransitionFlagKey(key);
                // The media-player row and the checkbox section are one value.
                const sessionValue = mediaRow
                  ? (shownMedia ?? rawSessionValue)
                  : rawSessionValue;
                const sessionEditorKind = flagEditorKind(sessionValue);
                const activeSourcePresent = simonSaysActive ? servicePresent : ldPresent;
                const sessionDiffersFromSource = hasSessionValue && (
                  !activeSourcePresent || !flagValuesEqual(sessionValue, activeSourceValue)
                );
                const mediaOverridden = mediaRow
                  && (mediaEdit != null || nonBooleanOverrideDiffers(
                    flagOverrides,
                    key,
                    activeSourceValue,
                    activeSourcePresent,
                  ))
                  && !mediaTransitionModelsEqual(shownMedia, activeSourceValue);
                const valueOverridden = mediaRow
                  ? mediaOverridden
                  : sessionDiffersFromSource || nonBooleanOverrideDiffers(
                    flagOverrides,
                    key,
                    activeSourceValue,
                    activeSourcePresent,
                  );
                const overridingValue = sessionEditorKind != null && valueOverridden;
                const overrideStored = flagHasStoredOverride(flagOverrides, key) || (mediaRow && mediaEdit != null);
                // The active column shows the session value, so a media-player
                // checkbox edit shows up on that flag. The other column stays
                // the raw source.
                const showSessionInActiveColumn = isMediaPlayerTransitionFlagKey(key)
                  ? sessionValue !== undefined
                  : hasSessionValue;
                const ldShown = !simonSaysActive && showSessionInActiveColumn ? sessionValue : ldValue;
                const ldShownPresent = simonSaysActive ? ldPresent : showSessionInActiveColumn || ldPresent;
                const serviceShown = simonSaysActive && showSessionInActiveColumn ? sessionValue : serviceValue;
                const serviceShownPresent = simonSaysActive ? showSessionInActiveColumn || servicePresent : servicePresent;
                const sessionOverride = (
                  <div className="flex h-full flex-row items-center justify-end gap-1 pr-1">
                    {typeof sessionValue === 'boolean' ? (
                      <Checkbox
                        onCheckedChange={(checked) => onFlagChange(key, checked)}
                        checked={sessionValue === true}
                      />
                    ) : sessionEditorKind && sessionValue !== undefined ? (
                      <Button
                        type="button"
                        size="sm"
                        style="solid"
                        color="secondary"
                        aria-label={overridingValue
                          ? t('internalTools.overridingFlagValueFor', { key })
                          : t('internalTools.editFlagValueFor', { key })}
                        onClick={() => openFlagEditor(key, sessionValue)}
                      >
                        {overridingValue
                          ? t('internalTools.overridingFlagValue')
                          : t('internalTools.editFlagValue')}
                      </Button>
                    ) : null}
                    {overrideStored ? (
                      <ResetOverrideButton flagKey={key} onReset={() => clearFlagOverride(key)} />
                    ) : null}
                    {valueOverridden ? <OverrideMark /> : null}
                  </div>
                );
                return (
                  <div
                    key={key}
                    className={`${flagGridClassName} box-border border-b border-solid border-white/30`}
                  >
                    <FlagSide
                      flagKey={key}
                      value={ldShown}
                      present={ldShownPresent}
                      onView={openFlagValue}
                    />
                    {simonSaysActive ? null : (
                      <>
                        <FlagColumnDivider />
                        {sessionOverride}
                      </>
                    )}
                    {showSimonSaysColumns ? (
                      <>
                        <FlagColumnDivider />
                        <div className="flex h-full items-center justify-center">
                          <button
                            type="button"
                            className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-inherit"
                            aria-label={equal
                              ? t('internalTools.compareMatch', { key })
                              : t('internalTools.compareDiffer', { key })}
                            onClick={() => openFlagCompare(key)}
                          >
                            <FlagCompareMark equal={equal} />
                          </button>
                        </div>
                        <FlagColumnDivider />
                        <ServiceFlagTooltip requestBody={serviceRequestBody}>
                          <FlagSide
                            flagKey={key}
                            value={serviceShown}
                            present={serviceShownPresent}
                            onView={openFlagValue}
                          />
                        </ServiceFlagTooltip>
                        {simonSaysActive ? (
                          <>
                            <FlagColumnDivider />
                            {sessionOverride}
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
            <div className="flex shrink-0 items-center justify-between gap-2">
              <h3 className="my-[5px] text-lg font-semibold text-[var(--tp-popover-fg)]">
                {t('internalTools.mediaTransition')}
              </h3>
              {mediaOrigin === 'override' ? (
                <ResetOverrideButton
                  flagKey={MEDIA_PLAYER_TRANSITION_FLAG_KEY}
                  onReset={() => clearFlagOverride(MEDIA_PLAYER_TRANSITION_FLAG_KEY)}
                />
              ) : null}
            </div>
            <p className="mb-1 min-w-0 shrink-0 break-all text-xs text-[var(--tp-popover-fg)]/70">
              {mediaOriginLabel}{' '}
              <code>{MEDIA_PLAYER_TRANSITION_FLAG_KEY}</code>
              {mediaValueText ? (
                <>
                  {' '}
                  (<code className="break-all">{mediaValueText}</code>)
                </>
              ) : null}
            </p>
            <div className="flex min-h-0 max-h-[280px] flex-1 flex-col gap-2 overflow-y-auto rounded-md bg-slate-900 px-2 pb-4 pt-3 text-white custom-scrollbar2">
              <div className="box-border flex flex-row items-center justify-between gap-1 pb-1 border-b border-solid border-white/30">
                <div className="break-all font-semibold">{t('internalTools.allViewers')}</div>
                <div className="flex flex-row items-center justify-end gap-1 pr-1">
                    <Checkbox
                    onCheckedChange={(checked) => patchMediaTransition({ enabled: checked })}
                    checked={mediaSession?.enabled === true}
                  />
                  {mediaOrigin === 'override' && (activeMedia?.enabled === true) !== (mediaSession?.enabled === true)
                    ? <OverrideMark />
                    : null}
                </div>
              </div>
              {MEDIA_PLAYER_TRANSITION_VIEWERS.map((viewer) => (
                <div
                  key={viewer}
                  className="box-border flex flex-row items-center justify-between gap-1 pb-1 border-b border-solid border-white/30"
                >
                  <div className="break-all">{MEDIA_PLAYER_TRANSITION_LABELS[viewer]}</div>
                  <div className="flex flex-row items-center justify-end gap-1 pr-1">
                    <Checkbox
                      onCheckedChange={(checked) => onMediaPlayerTransitionChange(viewer, checked)}
                      checked={mediaSession?.[viewer] === true}
                    />
                    {mediaOrigin === 'override' && (activeMedia?.[viewer] === true) !== (mediaSession?.[viewer] === true)
                      ? <OverrideMark />
                      : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {PROTOTYPE_FLAGS.length > 0 && (
            <>
              <h3 className="my-[5px] text-lg font-semibold text-[var(--tp-popover-fg)]">
                {t('prototypes.flagsHeading')}
              </h3>
              <div className="flex min-w-0 shrink-0 flex-col gap-2 pb-1">
                {PROTOTYPE_FLAGS.map(({ key, label }) => (
                  <div
                    key={key}
                    className="box-border flex flex-row items-center justify-between gap-1 border-b border-solid border-[var(--tp-popover-fg)]/30 pb-1"
                  >
                    <div
                      className={`break-all text-[var(--tp-popover-fg)] ${featureFlags?.[key] ? '' : 'opacity-70'}`}
                    >
                      {label}
                    </div>
                    <div className="flex shrink-0 flex-row items-center justify-end gap-1 pr-1">
                      <Checkbox
                        onCheckedChange={(checked) => onFlagChange(key, checked)}
                        checked={!!featureFlags?.[key]}
                      />
                      {flagHasStoredOverride(flagOverrides, key) ? (
                        <ResetOverrideButton flagKey={key} onReset={() => clearFlagOverride(key)} />
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      }
    >
      {/* AGENT: do not restyle this trigger. Canonical circular inverse
          icon button — no className/prop overrides, no duotone. */}
      <Button
        type="button"
        {...statusBarIconButtonProps}
        aria-label={t('internalTools.title')}
        className={statusBarIconButtonClassName}
        onClick={() => setIsOpen((current) => !current)}
      >
        <ToolboxIcon size={22} weight="regular" />
      </Button>
    </Popover>
    <Modal
      isOpen={viewingFlag != null}
      onOpenChange={(open) => {
        if (!open) closeFlagValue();
      }}
      title={viewingFlag?.key ?? t('internalTools.flagValue')}
      size="lg"
      // Internal Tools is a popover at z-index 100008. The modal overlay
      // defaults to 100000, which paints underneath that panel.
      className="z-[100010]"
    >
      {viewingFlag ? (
        <JsonViewer
          content={flagValueContent(viewingFlag.value)}
          showToolbar
          defaultExpandDepth={8}
          className="h-[min(60vh,520px)]"
        />
      ) : null}
    </Modal>
    <Modal
      isOpen={editingFlag != null}
      onOpenChange={(open) => {
        if (!open) closeFlagEditor();
      }}
      title={editingFlag?.key ?? t('internalTools.editFlagValue')}
      size="lg"
      className="z-[100010]"
    >
      {editingFlag ? (
        <div className="flex flex-col gap-3">
          {editingFlag.kind === 'number' ? (
            <input
              type="number"
              step="any"
              className="w-full rounded-md border border-solid border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
              aria-label={t('internalTools.editFlagValueFor', { key: editingFlag.key })}
              value={overrideDraft}
              onChange={(event) => {
                setOverrideDraft(event.target.value);
                setOverrideDraftError(false);
              }}
            />
          ) : (
            <textarea
              className={`w-full rounded-md border border-solid border-slate-300 bg-white p-3 font-mono text-sm text-slate-900 ${editingFlag.kind === 'json' ? 'min-h-[240px]' : 'min-h-[96px]'}`}
              spellCheck={false}
              aria-label={t('internalTools.editFlagValueFor', { key: editingFlag.key })}
              value={overrideDraft}
              onChange={(event) => {
                setOverrideDraft(event.target.value);
                setOverrideDraftError(false);
              }}
            />
          )}
          {overrideDraftError ? (
            <p className="m-0 text-sm text-red-600" role="alert">
              {editingFlag.kind === 'number'
                ? t('internalTools.invalidNumber')
                : t('internalTools.invalidJson')}
            </p>
          ) : null}
          <div className="flex flex-row justify-end gap-2">
            <Button type="button" size="sm" style="solid" color="secondary" onClick={closeFlagEditor}>
              {t('internalTools.cancelFlagEdit')}
            </Button>
            <Button type="button" size="sm" style="solid" color="secondary" onClick={saveFlagOverride}>
              {t('internalTools.saveFlagOverride')}
            </Button>
          </div>
        </div>
      ) : null}
    </Modal>
    <Modal
      isOpen={showSimonSaysColumns && comparingFlag != null}
      onOpenChange={(open) => {
        if (!open) closeFlagCompare();
      }}
      title={comparingFlag?.key ?? t('internalTools.compareFlag')}
      size="lg"
      className="z-[100010]"
    >
      {comparingFlag ? (
        <div className="grid grid-cols-2 gap-3">
          <ComparePane
            label={t('internalTools.fromLd')}
            flagKey={comparingFlag.key}
            value={comparingFlag.ld}
            present={comparingFlag.ldPresent}
          />
          <ComparePane
            label={t('internalTools.simonSays')}
            flagKey={comparingFlag.key}
            value={comparingFlag.service}
            present={comparingFlag.servicePresent}
          />
        </div>
      ) : null}
    </Modal>
    </>
  );
}
